import { useState, useRef, useEffect, useCallback, Component } from "react";
import { useApp } from "./context/AppContext";
import PDFViewer from "./components/pdf/PDFViewer";
import Navbar from "./components/layout/Navbar";
import PDFSelector from "./components/pdf/PDFSelector";
import "./App.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Workspace from "./components/workspace/Workspace";
import TraceLineLayer from "./components/workspace/TraceLineLayer";
import KeyboardShortcuts from "./components/layout/KeyboardShortcuts";
import useLayoutResizer from "./components/layout/useLayoutResizer";
import Toast from "./components/layout/Toast";
import AnnotationsSidebar from "./components/layout/AnnotationsSidebar";
import ScreenStickyNotes from "./components/layout/ScreenStickyNotes";
import BookmarksSidebar from "./components/layout/BookmarksSidebar";
import CrossPdfConnectionLayer from "./components/workspace/CrossPdfConnectionLayer";
import DocumentationPanel from "./components/documentation/DocumentationPanel";
import useDocumentationPages from "./hooks/useDocumentationPages";
import { getCurrentTimestampName } from "./utils/defaultNames";
import { PdfDropZone, PdfFileList, PdfUrlForm, mergeFiles } from "./components/pdf/PdfUpload";
import { fetchPdfBlobUrl, fileNameFromUrl } from "./utils/pdfFiles";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, info) {
        console.error("[ErrorBoundary]", error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 16, fontFamily: "sans-serif" }}>
                    <h2 style={{ color: "#dc2626" }}>Something went wrong</h2>
                    <p style={{ color: "#6b7280", fontSize: 14, maxWidth: 400, textAlign: "center" }}>
                        {this.state.error?.message || "An unexpected error occurred."}
                    </p>
                    <button
                        onClick={() => this.setState({ hasError: false, error: null })}
                        style={{ padding: "8px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14 }}
                    >
                        Try Again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// With the side-by-side panel open the row is [pdf1][divider][pdf2][divider][workspace].
// The three percentages must add up to 100 or pdf2 runs off the right edge and the
// workspace — the only shrinkable child — collapses to zero width.
const MIN_WORKSPACE_PCT = 16;   // sliver of notes kept visible beside two PDFs
const MIN_PANEL_PCT = 15;       // narrowest a PDF pane may get
const PANEL2_DEFAULT_PCT = 38;
const DEFAULT_SPLIT_PCT = 50;   // Split = PDF and notes half and half (also the startup layout)
const DIVIDER_PX = 6;

/** Widest the left pane may be for a given pdf2 width. A left pane asking for the whole
 *  row ("PDF only") means the workspace is deliberately hidden, so it reserves no share. */
const maxLeftWidth = (p2Width, wantsFullPdf) =>
    100 - p2Width - (wantsFullPdf ? 0 : MIN_WORKSPACE_PCT);

/** Inline workspace-name input to avoid browser prompt() */
function NewWorkspaceBtn({ onAdd }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState("");
    const openEditor = () => {
        setName(getCurrentTimestampName());
        setEditing(true);
    };

    if (editing) {
        return (
            <form
                onSubmit={e => {
                    e.preventDefault();
                    onAdd(name.trim() || getCurrentTimestampName());
                    setName("");
                    setEditing(false);
                }}
                style={{ display: "inline-flex", gap: 4, alignItems: "center" }}
            >
                <input
                    autoFocus
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onBlur={() => { setEditing(false); setName(""); }}
                    placeholder={getCurrentTimestampName()}
                    style={{ fontSize: 12, padding: "2px 6px", borderRadius: 6, border: "1px solid #aaa", width: 120 }}
                />
                <button type="submit" onMouseDown={e => e.preventDefault()} style={{ fontSize: 12, padding: "2px 8px", borderRadius: 6, border: "none", background: "#007bff", color: "white", cursor: "pointer" }}>Add</button>
            </form>
        );
    }
    return (
        <button className="add-workspace-btn" onClick={openEditor} title="Create New Workspace">+</button>
    );
}

/** "+" in the Files bar — adds more PDFs (uploaded or from a link) as new tabs. */
function OpenPdfModal({ onSelect, onSelectFiles, onClose }) {
    const [files, setFiles] = useState([]);
    const overlayRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const addFiles = () => {
        onSelectFiles(files); // keeps loading in the background after the modal closes
        onClose();
    };

    const loadUrl = async (url) => {
        const blobUrl = await fetchPdfBlobUrl(url);
        onClose();
        await onSelect(blobUrl, fileNameFromUrl(url), url);
    };

    return (
        <div ref={overlayRef} className="pdfu-overlay" onMouseDown={e => { if (e.target === overlayRef.current) onClose(); }}>
            <div className="pdfu-modal" role="dialog" aria-label="Add PDFs">
                <div className="pdfu-modal-head">
                    <span className="pdfu-modal-title">Add PDFs</span>
                    <button className="pdfu-icon-btn" onClick={onClose} title="Close"><i className="bi bi-x-lg" /></button>
                </div>

                <PdfDropZone compact onFiles={incoming => setFiles(prev => mergeFiles(prev, incoming))} />
                <PdfFileList files={files} onRemove={i => setFiles(prev => prev.filter((_, idx) => idx !== i))} />

                {files.length > 0 && (
                    <div className="pdfu-actions">
                        <button className="pdfu-btn pdfu-btn--primary" onClick={addFiles}>
                            <i className="bi bi-plus-lg" /> Add {files.length} PDF{files.length > 1 ? "s" : ""}
                        </button>
                    </div>
                )}

                <div className="pdfu-divider">or from a link</div>
                <PdfUrlForm onLoad={loadUrl} />
            </div>
        </div>
    );
}

// Active state for each view uses the same thresholds as switchLayout.
const LAYOUT_OPTIONS = [
    { label: "PDF", width: 100, title: "PDF only", isActive: w => w > 95 },
    { label: "Split", width: DEFAULT_SPLIT_PCT, title: "Split view — PDF and notes side by side", isActive: w => w > 5 && w < 95 },
    { label: "Notes", width: 0, title: "Workspace only", isActive: w => w < 5 },
];

export default function App() {
    const {
        loading,
        activeWorkspace,
        workspaces,
        setActiveWorkspace,
        selectedPDF,
        pdfPanelWidth, setPdfPanelWidth,
        isResizing,
        pdfRef, pdf2Ref,
        pdfId,
        handlePDFSelect,
        openLocalPdfFiles,
        openSavedWorkspace,
        handleAddWorkspace,
        pdfTabs, activeTabId,
        switchPdfTab, closePdfTab,
        panel2TabId, panel2PdfId, panel2PdfUrl, panel2PdfName,
        openInPanel2, closePanel2,
        isDirty,
    } = useApp();

    // Switching layout only changes the panel width — the PDF viewer's fit-to-width then
    // re-scales the page to the new panel width. (Previously this restored a remembered
    // per-mode zoom, which fought fit-to-width and left the page not filling the panel.)
    const switchLayout = (width) => {
        if (!panel2PdfUrl) { setPdfPanelWidth(width); return; }
        if (width <= 5) { setPdfPanelWidth(0); return; }
        setPdfPanelWidth(Math.max(MIN_PANEL_PCT, Math.min(width, maxLeftWidth(pdf2PanelWidth, width >= 95))));
    };

    // Warn user before closing tab if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);
    const [isDocumentationActive, setIsDocumentationActive] = useState(false);
    const {
        documents,
        activeDocumentId,
        saveStatus,
        createDocumentPage,
        selectDocument,
        renameDocument,
        deleteDocument,
        updateDocumentContent,
    } = useDocumentationPages(activeWorkspace?.id ?? null);

    const [showOpenModal, setShowOpenModal] = useState(false);
    const [pdf2PanelWidth, setPdf2PanelWidth] = useState(PANEL2_DEFAULT_PCT); // right panel width %
    const [pdf2Zoom, setPdf2Zoom] = useState(1.0);            // right panel independent zoom
    const [isResizing2, setIsResizing2] = useState(false);
    const resizer2Ref = useRef({ startX: 0, startWidth: 35, startZoom: 1.0 });
    const leftWidthBeforePanel2 = useRef(null);

    // Opening the side-by-side panel has to take its width from the left pane — otherwise
    // the columns total more than 100% and pdf2 is clipped off the right edge. Closing it
    // hands that width back.
    const handleOpenInPanel2 = useCallback((tab) => {
        const wantsFullPdf = pdfPanelWidth >= 95;
        const p2 = Math.min(Math.max(pdf2PanelWidth, MIN_PANEL_PCT), 50);
        const left = pdfPanelWidth <= 5
            ? 0
            : Math.max(MIN_PANEL_PCT, Math.min(pdfPanelWidth, maxLeftWidth(p2, wantsFullPdf)));
        leftWidthBeforePanel2.current = { was: pdfPanelWidth, lent: left };
        setPdfPanelWidth(left);
        setPdf2PanelWidth(Math.min(p2, 100 - left - (wantsFullPdf ? 0 : MIN_WORKSPACE_PCT)));
        openInPanel2(tab);
    }, [pdfPanelWidth, pdf2PanelWidth, setPdfPanelWidth, openInPanel2]);

    // Panel 2 also closes indirectly (its tab gets closed, a workspace reload clears it), so
    // the borrowed width is handed back from an effect rather than only from the × button.
    // Skipped if the user re-dragged the divider meanwhile — that width is theirs to keep.
    useEffect(() => {
        const borrowed = leftWidthBeforePanel2.current;
        if (panel2PdfUrl || !borrowed) return;
        if (Math.abs(pdfPanelWidth - borrowed.lent) < 0.5) setPdfPanelWidth(borrowed.was);
        leftWidthBeforePanel2.current = null;
    }, [panel2PdfUrl, pdfPanelWidth, setPdfPanelWidth]);

    const [showPdfDropdown, setShowPdfDropdown] = useState(false);
    const [hasTabOverflow, setHasTabOverflow] = useState(false);
    const tabsScrollRef = useRef(null);
    const pdfDropdownRef = useRef(null);

    // Detect tab overflow
    useEffect(() => {
        const el = tabsScrollRef.current;
        if (!el) return;
        const check = () => setHasTabOverflow(el.scrollWidth > el.clientWidth + 2);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [pdfTabs]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!showPdfDropdown) return;
        const handler = (e) => {
            if (pdfDropdownRef.current && !pdfDropdownRef.current.contains(e.target)) {
                setShowPdfDropdown(false);
            }
        };
        document.addEventListener("pointerdown", handler);
        return () => document.removeEventListener("pointerdown", handler);
    }, [showPdfDropdown]);
    // Update browser tab title to active PDF name
    useEffect(() => {
        const activeTab = pdfTabs.find(t => t.tabId === activeTabId);
        document.title = activeTab?.name ? activeTab.name : "PDF Reader";
    }, [pdfTabs, activeTabId]);

    const getWorkspaceTabLabel = useCallback((name) => {
        if ((name || "").trim().toLowerCase() === "main") return "Judge note";
        return name;
    }, []);

    // Drag limit for the pdf1/pdf2 divider — with the side panel open pdf1 can only claim
    // what pdf2 and the workspace sliver leave over.
    const { handleMouseDownResizer, handleTouchStartResizer } = useLayoutResizer(
        panel2PdfUrl ? { minPct: MIN_PANEL_PCT, maxPct: maxLeftWidth(pdf2PanelWidth, false) } : undefined
    );

    // The two dividers are 6px each and sit outside the percentage budget, so each PDF pane
    // gives one back. Without this the panes total 100% + 12px and pdf2 clips off-screen.
    const pdf1Width = panel2PdfUrl
        ? (pdfPanelWidth === 0 ? '0px' : `calc(${pdfPanelWidth}% - ${DIVIDER_PX}px)`)
        : (pdfPanelWidth === 100 ? `calc(100% - ${DIVIDER_PX}px)` : (pdfPanelWidth === 0 ? '0px' : `${pdfPanelWidth}%`));

    // The drag handle only makes sense in Split — in PDF-only or Notes-only there is
    // nothing on the other side, and the floating pill just sits in the reader's way.
    // Same thresholds the PDF/Split/Notes toggle uses to decide which button is active.
    const isSplitLayout = pdfPanelWidth > 5 && pdfPanelWidth < 95;

    // Right-panel drag resizer — also scales pdf2Zoom proportionally like the main resizer does for PDF1
    const handleMouseDownResizer2 = useCallback((e) => {
        setIsResizing2(true);
        resizer2Ref.current = { startX: e.clientX, startWidth: pdf2PanelWidth, startZoom: pdf2Zoom };
    }, [pdf2PanelWidth, pdf2Zoom]);

    useEffect(() => {
        if (!isResizing2) return;
        const onMove = (e) => {
            const { startX, startWidth, startZoom } = resizer2Ref.current;
            const delta = e.clientX - startX;
            const deltaPct = (delta / window.innerWidth) * 100;
            // Cap at whatever the left pane and the workspace sliver leave over, so dragging
            // this divider can never push pdf2 off the right edge.
            const cap = Math.max(MIN_PANEL_PCT, 100 - pdfPanelWidth - MIN_WORKSPACE_PCT);
            const next = Math.min(cap, Math.max(MIN_PANEL_PCT, startWidth + deltaPct));
            setPdf2PanelWidth(next);
            // Scale zoom proportionally so PDF content fills the resized panel
            const ratio = next / startWidth;
            setPdf2Zoom(Math.min(3.0, Math.max(0.3, parseFloat((startZoom * ratio).toFixed(2)))));
        };
        const onUp = () => setIsResizing2(false);
        // Pointer events: mouse, finger (iPad) and pen all drive the divider
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };
    }, [isResizing2, pdfPanelWidth]);

    const renderWorkspaceView = () => {
        if (!selectedPDF && pdfTabs.length === 0) {
            return <PDFSelector onSelect={handlePDFSelect} onSelectFiles={openLocalPdfFiles} onOpenWorkspace={openSavedWorkspace} />;
        }

        return (
            <>
                <KeyboardShortcuts />
                <AnnotationsSidebar />
                <BookmarksSidebar />
                {!isDocumentationActive && <ScreenStickyNotes />}
                {!isDocumentationActive && <TraceLineLayer />}
                {!isDocumentationActive && <CrossPdfConnectionLayer />}

                {showOpenModal && (
                    <OpenPdfModal
                        onSelect={handlePDFSelect}
                        onSelectFiles={openLocalPdfFiles}
                        onClose={() => setShowOpenModal(false)}
                    />
                )}

                {loading && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <div style={{ background: 'white', padding: '10px 20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                            Loading...
                        </div>
                    </div>
                )}
                <Navbar />

                <div className="context-bar">
                    <div
                        className="pdf-tabs-container"
                        style={{
                            // Spans both PDF panes plus the divider between them.
                            width: panel2PdfUrl
                                ? `calc(${pdfPanelWidth + pdf2PanelWidth}% - ${DIVIDER_PX}px)`
                                : `${pdfPanelWidth}%`,
                            display: !panel2PdfUrl && pdfPanelWidth === 0 ? 'none' : 'flex',
                        }}
                    >
                        <span className="context-label" style={{ paddingLeft: '16px', flexShrink: 0 }}>Files:</span>
                        <div
                            ref={tabsScrollRef}
                            className="workspace-tabs-scroll"
                            style={{
                                paddingLeft: 0, gap: 2,
                                overflowX: "hidden", overflowY: "hidden",
                                flexWrap: "nowrap",
                                flex: 1,
                            }}
                        >
                            {pdfTabs.map(tab => (
                                <div
                                    key={tab.tabId}
                                    className={`workspace-tab-item ${tab.tabId === activeTabId ? 'active' : ''}`}
                                    onClick={() => switchPdfTab(tab)}
                                    title={tab.name}
                                    style={{
                                        marginTop: 4,
                                        display: "flex", alignItems: "center", gap: 3,
                                        padding: "2px 4px 2px 6px",
                                        maxWidth: 130,
                                        minWidth: 60,
                                        flexShrink: 0,
                                        cursor: "pointer",
                                        background: tab.tabId === activeTabId ? "#e8f4ff" : "transparent",
                                        borderRadius: 5,
                                    }}
                                >
                                    <div style={{
                                        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                                        background: tab.color || "#9ca3af",
                                        boxShadow: tab.tabId === activeTabId ? `0 0 0 2px ${tab.color || "#9ca3af"}44` : "none",
                                    }} />
                                    <span className="tab-name" style={{
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        fontWeight: tab.tabId === activeTabId ? 600 : 400,
                                        color: tab.tabId === activeTabId ? (tab.color || "#0057c8") : "#374151",
                                        fontSize: 11,
                                        flex: 1,
                                    }}>{tab.name}</span>
                                    {pdfTabs.length > 1 && (
                                        <button
                                            onClick={e => {
                                                e.stopPropagation();
                                                if (panel2TabId === tab.tabId) { closePanel2(); }
                                                else { handleOpenInPanel2(tab); }
                                            }}
                                            title={panel2TabId === tab.tabId ? "Close right panel" : "Open in right panel (side-by-side)"}
                                            style={{
                                                border: "none", background: "none", cursor: "pointer",
                                                color: panel2TabId === tab.tabId ? (tab.color || "#007bff") : "#9ca3af",
                                                fontSize: 10, lineHeight: 1, padding: "0 1px", flexShrink: 0,
                                                fontWeight: 700,
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.color = tab.color || "#007bff"}
                                            onMouseLeave={e => e.currentTarget.style.color = panel2TabId === tab.tabId ? (tab.color || "#007bff") : "#9ca3af"}
                                        >⊞</button>
                                    )}
                                    <button
                                        onClick={e => { e.stopPropagation(); closePdfTab(tab.tabId); }}
                                        title="Close this PDF"
                                        style={{
                                            border: "none", background: "none", cursor: "pointer",
                                            color: "#9ca3af", fontSize: 13, lineHeight: 1,
                                            padding: "0 1px", flexShrink: 0,
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                                        onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
                                    >×</button>
                                </div>
                            ))}
                        </div>

                        <button
                            className="pdf-add-tab-btn"
                            onClick={() => setShowOpenModal(true)}
                            title="Add more PDFs"
                        ><i className="bi bi-plus-lg" /></button>

                        {/* Overflow dropdown — appears when tabs don't all fit */}
                        {hasTabOverflow && (
                            <div ref={pdfDropdownRef} style={{ position: "relative", flexShrink: 0 }}>
                                <button
                                    onClick={() => setShowPdfDropdown(v => !v)}
                                    title="All open PDFs"
                                    style={{
                                        border: "none", background: showPdfDropdown ? "#e8f4ff" : "transparent",
                                        cursor: "pointer", padding: "2px 6px", height: 28,
                                        borderRadius: 4, display: "flex", alignItems: "center",
                                        color: "#374151", fontSize: 12, fontWeight: 600,
                                    }}
                                >
                                    ▾
                                </button>
                                {showPdfDropdown && (
                                    <div style={{
                                        position: "absolute", top: "calc(100% + 2px)", right: 0,
                                        background: "#fff", border: "1px solid #dadce0",
                                        borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                        minWidth: 200, maxHeight: 280, overflowY: "auto",
                                        zIndex: 9999,
                                    }}>
                                        {pdfTabs.map(tab => (
                                            <div
                                                key={tab.tabId}
                                                onClick={() => { switchPdfTab(tab); setShowPdfDropdown(false); }}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 8,
                                                    padding: "7px 12px", cursor: "pointer",
                                                    background: tab.tabId === activeTabId ? "#e8f4ff" : "transparent",
                                                    borderBottom: "1px solid #f3f4f6",
                                                }}
                                                onMouseEnter={e => { if (tab.tabId !== activeTabId) e.currentTarget.style.background = "#f5f5f5"; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = tab.tabId === activeTabId ? "#e8f4ff" : "transparent"; }}
                                            >
                                                <div style={{
                                                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                                    background: tab.color || "#9ca3af",
                                                }} />
                                                <span style={{
                                                    fontSize: 12, flex: 1,
                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    fontWeight: tab.tabId === activeTabId ? 600 : 400,
                                                    color: tab.tabId === activeTabId ? (tab.color || "#0057c8") : "#374151",
                                                }}>{tab.name}</span>
                                                <button
                                                    onClick={e => { e.stopPropagation(); closePdfTab(tab.tabId); if (pdfTabs.length <= 1) setShowPdfDropdown(false); }}
                                                    title="Close"
                                                    style={{
                                                        border: "none", background: "none", cursor: "pointer",
                                                        color: "#9ca3af", fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0,
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                                                    onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
                                                >×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="workspace-tabs-container">
                        {/* <span className="context-label">Workspaces:</span> */}
                        <div className="workspace-tabs-scroll">
                            {workspaces.map(ws => (
                                <div
                                    key={ws.id}
                                    className={`context-tab workspace-tab-item ${!isDocumentationActive && activeWorkspace?.id === ws.id ? 'active' : ''}`}
                                    onClick={() => {
                                        setIsDocumentationActive(false);
                                        setActiveWorkspace(ws);
                                    }}
                                    title={ws.name}
                                >
                                    {!isDocumentationActive && activeWorkspace?.id === ws.id && (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                    <span className="tab-name">{getWorkspaceTabLabel(ws.name)}</span>
                                </div>
                            ))}
                            <div
                                className={`context-tab workspace-tab-item ${isDocumentationActive ? 'active' : ''}`}
                                onClick={() => setIsDocumentationActive(true)}
                                title="Editor"
                            >
                                {isDocumentationActive && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                )}
                                <span className="tab-name">Word</span>
                                <span className="workspace-tab-count">{documents.length}</span>
                            </div>
                            <NewWorkspaceBtn onAdd={handleAddWorkspace} />
                        </div>
                    </div>
                </div>

                <div className="main-content" style={{ position: 'relative', cursor: isResizing || isResizing2 ? 'col-resize' : 'default' }}>
                    {/* PDF 1 — always left */}
                    <div className={`pdf-view-container ${isResizing ? 'is-resizing' : ''}`} style={{ width: pdf1Width, height: '100%', flex: 'none', position: 'relative' }}>
                        <PDFViewer ref={pdfRef} fileUrl={selectedPDF} sourcePdfId={pdfId} fitKey={panel2PdfUrl ? 'p2' : 'solo'} />
                    </div>

                    <div
                        className={`layout-resizer ${isResizing ? 'active' : ''}`}
                        onMouseDown={handleMouseDownResizer}
                        onTouchStart={handleTouchStartResizer}
                    >
                        {isSplitLayout && (
                            <div
                                className="resizer-handle"
                                style={{
                                    pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    height: 48, width: 24, cursor: 'col-resize',
                                    transition: 'transform 0.2s ease',
                                    borderRadius: '6px'
                                }}
                                onDoubleClick={(e) => { e.stopPropagation(); switchLayout(DEFAULT_SPLIT_PCT); }}
                                title="Drag to resize · Double-click to reset to 50/50"
                            >
                                <div style={{ lineHeight: 1, fontSize: 14 }}>⋮</div>
                            </div>
                        )}
                    </div>

                    {/* PDF 2 — middle when open, placed before workspace */}
                    {panel2PdfUrl && (
                        <>
                            <div style={{
                                width: `calc(${pdf2PanelWidth}% - ${DIVIDER_PX}px)`, height: '100%', flex: 'none',
                                position: 'relative', display: 'flex', flexDirection: 'column',
                                borderLeft: `3px solid ${pdfTabs.find(t => t.tabId === panel2TabId)?.color || '#e5e7eb'}`,
                                borderRight: `3px solid ${pdfTabs.find(t => t.tabId === panel2TabId)?.color || '#e5e7eb'}`,
                            }}>
                                <div style={{
                                    height: 32, flexShrink: 0, display: 'flex', alignItems: 'center',
                                    padding: '0 8px', gap: 4,
                                    background: '#f8fafc', borderBottom: '1px solid #e5e7eb',
                                }}>
                                    <div style={{
                                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                        background: pdfTabs.find(t => t.tabId === panel2TabId)?.color || '#9ca3af',
                                    }} />
                                    <span style={{
                                        flex: 1, fontSize: 11, fontWeight: 600, color: '#374151',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>{panel2PdfName}</span>
                                    <button onClick={() => setPdf2Zoom(z => Math.max(0.3, parseFloat((z - 0.15).toFixed(2))))}
                                        title="Zoom out" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#6b7280', padding: '0 3px', lineHeight: 1 }}>−</button>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', minWidth: 30, textAlign: 'center' }}>{Math.round(pdf2Zoom * 100)}%</span>
                                    <button onClick={() => setPdf2Zoom(z => Math.min(3.0, parseFloat((z + 0.15).toFixed(2))))}
                                        title="Zoom in" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#6b7280', padding: '0 3px', lineHeight: 1 }}>+</button>
                                    <button
                                        onClick={closePanel2}
                                        title="Close PDF 2 panel"
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, lineHeight: 1, padding: '0 2px', marginLeft: 2 }}
                                        onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                        onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                                    >×</button>
                                </div>

                                <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                                    <PDFViewer
                                        key={panel2TabId}
                                        ref={pdf2Ref}
                                        fileUrl={panel2PdfUrl}
                                        sourcePdfId={panel2PdfId}
                                        localZoom={pdf2Zoom}
                                        setLocalZoom={setPdf2Zoom}
                                    />
                                </div>
                            </div>

                            <div
                                style={{
                                    width: 6, height: '100%', cursor: 'col-resize', flexShrink: 0,
                                    touchAction: 'none', // iPad: drag the divider instead of scrolling
                                    background: isResizing2 ? '#93c5fd' : '#e5e7eb',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background 0.15s',
                                }}
                                onPointerDown={handleMouseDownResizer2}
                                onMouseEnter={e => e.currentTarget.style.background = '#93c5fd'}
                                onMouseLeave={e => { if (!isResizing2) e.currentTarget.style.background = '#e5e7eb'; }}
                            >
                                <span style={{ fontSize: 10, color: '#9ca3af', userSelect: 'none' }}>⋮</span>
                            </div>
                        </>
                    )}

                    {/* Workspace — always rightmost */}
                    <div className="workspace-view-wrapper" style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                        {isDocumentationActive ? (
                            <DocumentationPanel
                                documents={documents}
                                activeDocumentId={activeDocumentId}
                                saveStatus={saveStatus}
                                onSelectDocument={selectDocument}
                                onCreateDocument={createDocumentPage}
                                onRenameDocument={renameDocument}
                                onDeleteDocument={deleteDocument}
                                onUpdateDocumentContent={updateDocumentContent}
                            />
                        ) : (
                            <Workspace />
                        )}
                    </div>

                    {/* PDF / Split / Notes — floats bottom-right over whichever pane is
                        rightmost, so it stays reachable in PDF-only mode too. The workspace
                        HUD (bottom: 70) and the PDF zoom pill (bottom: 64) sit above it. */}
                    <div style={{
                        position: 'absolute',
                        bottom: 20,
                        right: 20,
                        zIndex: 1001,
                        display: 'flex',
                        background: 'rgba(255, 255, 255, 0.65)',
                        backdropFilter: 'blur(16px) saturate(180%)',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: 10,
                        padding: 4,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)'
                    }}>
                        {LAYOUT_OPTIONS.map(({ label, width, title, isActive }) => {
                            const active = isActive(pdfPanelWidth);
                            return (
                                <button
                                    key={label}
                                    style={{ border: 'none', background: active ? '#fff' : 'transparent', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 700, color: active ? '#1a73e8' : '#5f6368', cursor: 'pointer', boxShadow: active ? '0 2px 6px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
                                    onClick={() => switchLayout(width)}
                                    title={title}
                                >{label}</button>
                            );
                        })}
                    </div>
                </div>
            </>
        );
    };

    return (
        <ErrorBoundary>
            <div className={`app-container ${selectedPDF || pdfTabs.length ? "has-toolbar" : ""}`}>
                <Toast />
                {renderWorkspaceView()}
            </div>
        </ErrorBoundary>
    );
}
